import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { auditApi } from "../api/audit.api.js";
import { AuditRow, Card, ErrorState, PageHeader, SkeletonList, buttonBase, colors, } from "../components/ui/index.js";
import { AuditFilterChips } from "../components/audit/AuditFilterChips.js";
const PAGE_SIZE = 25;
const SINCE_DAYS = 30;
export function AuditPage() {
    // Default filter window: last 30 days. Computed once per mount so the cache
    // key stays stable across paginations.
    const [since] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - SINCE_DAYS);
        return d.toISOString();
    });
    const [selectedPrefixes, setSelectedPrefixes] = useState([]);
    const [page, setPage] = useState(1);
    const q = useQuery({
        queryKey: ["audit", since, selectedPrefixes],
        queryFn: () => auditApi.list({
            since,
            types: selectedPrefixes.length > 0 ? selectedPrefixes : undefined,
        }),
    });
    const entries = useMemo(() => {
        if (!q.data)
            return [];
        // Defensive: server should pre-filter, but apply client-side prefix
        // filter as well so chips remain responsive even if cache is reused.
        if (selectedPrefixes.length === 0)
            return q.data;
        return q.data.filter((e) => selectedPrefixes.some((prefix) => e.type.startsWith(prefix)));
    }, [q.data, selectedPrefixes]);
    const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageEntries = entries.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const handleChange = (next) => {
        setSelectedPrefixes(next);
        setPage(1);
    };
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Audit log" }), _jsx(Card, { style: { marginBottom: 12 }, children: _jsxs("div", { style: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                    }, children: [_jsx(AuditFilterChips, { selected: selectedPrefixes, onChange: handleChange }), _jsxs("span", { style: { color: colors.textTertiary, fontSize: "0.75rem" }, children: ["Showing entries from the last ", SINCE_DAYS, " days"] })] }) }), q.isLoading ? (_jsx(SkeletonList, { rows: 6 })) : q.error ? (_jsx(ErrorState, { message: "Failed to load audit log" })) : entries.length === 0 ? (_jsx(Card, { children: _jsx("p", { style: { margin: 0, color: colors.textSecondary }, children: "No audit entries match the current filter." }) })) : (_jsxs(Card, { style: { padding: 0, overflow: "hidden" }, children: [pageEntries.map((entry, i) => (_jsx(AuditRow, { entry: entry, linkable: true, topBorder: i !== 0 }, entry.id))), totalPages > 1 && (_jsxs("div", { style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                            padding: "10px 12px",
                            background: colors.surfaceSecondary,
                            borderTop: `1px solid ${colors.border}`,
                            fontSize: "0.8rem",
                        }, children: [_jsxs("span", { style: { color: colors.textSecondary }, children: ["Page ", safePage, " of ", totalPages, " \u00B7 ", entries.length, " entries"] }), _jsxs("span", { style: { display: "flex", gap: 6 }, children: [_jsx("button", { type: "button", onClick: () => setPage((p) => Math.max(1, p - 1)), disabled: safePage <= 1, style: {
                                            ...buttonBase,
                                            background: colors.surface,
                                            color: colors.text,
                                            border: `1px solid ${colors.border}`,
                                            opacity: safePage <= 1 ? 0.5 : 1,
                                            cursor: safePage <= 1 ? "not-allowed" : "pointer",
                                        }, children: "Previous" }), _jsx("button", { type: "button", onClick: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: safePage >= totalPages, style: {
                                            ...buttonBase,
                                            background: colors.surface,
                                            color: colors.text,
                                            border: `1px solid ${colors.border}`,
                                            opacity: safePage >= totalPages ? 0.5 : 1,
                                            cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                                        }, children: "Next" })] })] }))] }))] }));
}
